<template>
  <div
    :role="tone === 'error' ? 'alert' : 'status'"
    class="flex gap-2.5 rounded-[10px] border"
    :class="[TONES[tone], compact ? 'items-start px-3 py-2 text-xs font-medium' : 'items-start px-3.5 py-[11px] text-sm']"
  >
    <!-- The one inline message for a form or a panel (the alerts redesign): the
         tone's icon, dark tone text on its tint, a hairline border in the tone,
         an optional title and one action. Errors are announced as alerts. -->
    <component :is="ICONS[tone]" :size="compact ? 15 : 18" class="shrink-0 mt-px" :class="ICON_COLOURS[tone]" aria-hidden="true" />
    <div class="flex-1 min-w-0 flex flex-col gap-1 leading-5" :class="compact ? 'leading-4' : ''">
      <p v-if="title" class="font-semibold">{{ title }}</p>
      <div><slot /></div>
      <div v-if="$slots.actions" class="flex flex-wrap items-center gap-4 mt-1"><slot name="actions" /></div>
    </div>
    <!-- One action beside a one-line message (Resend email). -->
    <div v-if="$slots.action" class="shrink-0 -my-1.5 -mr-1.5 self-center"><slot name="action" /></div>
    <button
      v-if="dismissible"
      type="button"
      aria-label="Dismiss"
      class="shrink-0 -my-0.5 -mr-1 p-0.5 rounded opacity-70 hover:opacity-100 transition-opacity"
      @click="$emit('dismiss')"
    >
      <X aria-hidden="true" :size="14" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-vue-next'

export type NoticeTone = 'error' | 'warning' | 'success' | 'info'

withDefaults(defineProps<{
  tone?: NoticeTone
  title?: string
  /** Shows an X; the parent hides the notice on `dismiss`. */
  dismissible?: boolean
  /** 12px, for narrow panels (the editor's photo panel). */
  compact?: boolean
}>(), { tone: 'error', title: '', dismissible: false, compact: false })

defineEmits<{ dismiss: [] }>()

const TONES = {
  error: 'bg-error-bg border-error-border text-error-ink',
  warning: 'bg-warning-bg border-warning-border text-warning-ink',
  success: 'bg-success-bg border-success-border text-success-ink',
  info: 'bg-info-bg border-info-border text-info-ink',
} as const
// The bright fill only on the icon, where 3:1 is enough; one shape per tone, so tones differ without colour.
const ICON_COLOURS = { error: 'text-error', warning: 'text-warning', success: 'text-success', info: 'text-info' } as const
const ICONS = { error: CircleAlert, warning: TriangleAlert, success: CircleCheck, info: Info } as const
</script>
